// ═══════════════════════════════════════════
// Seeded Random — deterministic procedural generation
// ═══════════════════════════════════════════
export function seededRandom(seed) {
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
        h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
    }
    return () => {
        h = (h * 1664525 + 1013904223) | 0;
        return (h >>> 0) / 4294967296;
    };
}
export function seededPick(arr, rng) {
    return arr[Math.floor(rng() * arr.length)];
}
function seededRange(min, max, rng) {
    return Math.floor(rng() * (max - min + 1)) + min;
}
// Level Caps
export const LEVEL_CAPS = {
    player: 50,
    unit: 20,
    structure: 10,
    alliance: 50,
    allianceTech: 10,
};
// Integrity Bounds
export const INTEGRITY_BOUNDS = {
    min: 0,
    max: 100,
};
// AI Difficulty Bounds
export const AI_DIFFICULTY_BOUNDS = {
    min: 1,
    max: 50,
};
// Game Configuration
export const GAME_CONFIG = {
    ZONE_CAPTURE_TIME: 30000, // 30 seconds
    ZONE_RADIUS: 100, // meters
    BATTLE_DURATION: 90000, // 90 seconds
    BATTLE_TICK_RATE: 100, // milliseconds
    SHIELD_COOLDOWN: 7200000, // 2 hours
    GPS_UPDATE_INTERVAL: 5000, // 5 seconds
    MAX_ZONES_BASE: 5,
    MIN_UNITS_IN_BATTLE: 1,
    MAX_UNITS_IN_BATTLE: 3,
};
// Resource Display Configuration — single source of truth for resource presentation
export const RESOURCE_CONFIG = {
    energy: {
        name: 'Energy',
        shortName: 'NRG',
        symbol: '\u26A1', // ⚡
        color: 'text-amber-400',
        bgColor: 'bg-amber-400',
    },
    nanomaterials: {
        name: 'Nanomaterials',
        shortName: 'NANO',
        symbol: '\u25C6', // ◆
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-400',
    },
    quantum: {
        name: 'Quantum',
        shortName: 'QTM',
        symbol: '\u25C8', // ◈
        color: 'text-violet-400',
        bgColor: 'bg-violet-400',
    },
    darkMatter: {
        name: 'Dark Matter',
        shortName: 'DRK',
        symbol: '\u263E', // ☾
        color: 'text-rose-400',
        bgColor: 'bg-rose-400',
    },
};
// Resource keys in display order
export const RESOURCE_ORDER = ['energy', 'nanomaterials', 'quantum', 'darkMatter'];
// Format a ResourceAmount as a compact cost string (e.g. "⚡50 ◆20 ◈10")
export function formatCost(cost) {
    const parts = [];
    for (const key of RESOURCE_ORDER) {
        if (cost[key] > 0) {
            parts.push(`${RESOURCE_CONFIG[key].symbol}${cost[key]}`);
        }
    }
    return parts.join(' ');
}
// Check if a player can afford a cost
export function canAffordCost(resources, cost) {
    return (resources.energy >= cost.energy &&
        resources.nanomaterials >= cost.nanomaterials &&
        resources.quantum >= cost.quantum &&
        resources.darkMatter >= cost.darkMatter);
}
// Unit Display Metadata
export const UNIT_META = {
    scout_drone: { name: 'Scout Drone', shortName: 'Scout', color: 'text-sky-400' },
    assault_bot: { name: 'Assault Bot', shortName: 'Assault', color: 'text-red-400' },
    heavy_mech: { name: 'Heavy Mech', shortName: 'Heavy', color: 'text-orange-400' },
    nano_swarm: { name: 'Nano Swarm', shortName: 'Swarm', color: 'text-emerald-400' },
    emp_specialist: { name: 'EMP Specialist', shortName: 'EMP', color: 'text-violet-400' },
    titan_mech: { name: 'Titan Mech', shortName: 'Titan', color: 'text-amber-400' },
};
// Unit Base Stats
export const UNIT_STATS = {
    scout_drone: {
        hp: 60,
        atk: 40,
        def: 20,
        spd: 90,
        acc: 85,
        energy: 50,
    },
    assault_bot: {
        hp: 80,
        atk: 70,
        def: 40,
        spd: 60,
        acc: 75,
        energy: 60,
    },
    heavy_mech: {
        hp: 120,
        atk: 60,
        def: 70,
        spd: 30,
        acc: 65,
        energy: 40,
    },
    nano_swarm: {
        hp: 70,
        atk: 30,
        def: 30,
        spd: 70,
        acc: 80,
        energy: 80,
    },
    emp_specialist: {
        hp: 65,
        atk: 50,
        def: 35,
        spd: 75,
        acc: 90,
        energy: 70,
    },
    titan_mech: {
        hp: 200,
        atk: 90,
        def: 80,
        spd: 20,
        acc: 70,
        energy: 50,
    },
};
// Unit Costs — authoritative values used by both server and client
export const UNIT_COSTS = {
    scout_drone: {
        energy: 20,
        nanomaterials: 10,
        quantum: 0,
        darkMatter: 0,
    },
    assault_bot: {
        energy: 40,
        nanomaterials: 25,
        quantum: 0,
        darkMatter: 0,
    },
    heavy_mech: {
        energy: 80,
        nanomaterials: 50,
        quantum: 0,
        darkMatter: 0,
    },
    nano_swarm: {
        energy: 60,
        nanomaterials: 40,
        quantum: 0,
        darkMatter: 0,
    },
    emp_specialist: {
        energy: 50,
        nanomaterials: 30,
        quantum: 0,
        darkMatter: 0,
    },
    titan_mech: {
        energy: 150,
        nanomaterials: 100,
        quantum: 0,
        darkMatter: 0,
    },
};
// Structure Costs
export const STRUCTURE_COSTS = {
    laser_turret: {
        energy: 50,
        nanomaterials: 20,
        quantum: 0,
        darkMatter: 0,
    },
    shield_generator: {
        energy: 100,
        nanomaterials: 50,
        quantum: 10,
        darkMatter: 0,
    },
    stealth_field: {
        energy: 200,
        nanomaterials: 100,
        quantum: 25,
        darkMatter: 0,
    },
    mining_rig: {
        energy: 75,
        nanomaterials: 0,
        quantum: 0,
        darkMatter: 0,
    },
    research_lab: {
        energy: 150,
        nanomaterials: 75,
        quantum: 20,
        darkMatter: 0,
    },
    headquarters: {
        energy: 500,
        nanomaterials: 200,
        quantum: 50,
        darkMatter: 0,
    },
    teleport_gate: {
        energy: 300,
        nanomaterials: 150,
        quantum: 40,
        darkMatter: 5,
    },
};
// Structure Display Metadata
export const STRUCTURE_META = {
    laser_turret: {
        name: 'Laser Turret',
        shortName: 'Turret',
        description: 'Deals damage to enemies each turn',
        color: 'text-red-400',
        glowColor: 'rgba(239, 68, 68, 0.5)',
    },
    shield_generator: {
        name: 'Shield Generator',
        shortName: 'Shield',
        description: 'Reduces incoming damage 3%/lv',
        color: 'text-blue-400',
        glowColor: 'rgba(59, 130, 246, 0.5)',
    },
    stealth_field: {
        name: 'Stealth Field',
        shortName: 'Stealth',
        description: 'Reduces enemy accuracy 3%/lv',
        color: 'text-violet-400',
        glowColor: 'rgba(139, 92, 246, 0.5)',
    },
    mining_rig: {
        name: 'Mining Rig',
        shortName: 'Mine',
        description: '+10–55% resource generation',
        color: 'text-amber-400',
        glowColor: 'rgba(251, 191, 36, 0.5)',
    },
    research_lab: {
        name: 'Research Lab',
        shortName: 'Lab',
        description: 'Lv3: EMP, Lv5: Titan unlock',
        color: 'text-emerald-400',
        glowColor: 'rgba(34, 197, 94, 0.5)',
    },
    headquarters: {
        name: 'Headquarters',
        shortName: 'HQ',
        description: 'Cannot demolish, +2%/lv unit HP',
        color: 'text-cyan-400',
        glowColor: 'rgba(34, 211, 238, 0.5)',
    },
    teleport_gate: {
        name: 'Teleport Gate',
        shortName: 'Gate',
        description: 'Deploy to any gated zone',
        color: 'text-pink-400',
        glowColor: 'rgba(236, 72, 153, 0.5)',
    },
};
// Unit Status Display Config
export const STATUS_CONFIG = {
    idle: {
        color: 'text-slate-400',
        bg: 'bg-slate-500/20',
        dotColor: 'bg-slate-500',
        glowColor: 'rgba(148, 163, 184, 0.3)',
        label: 'Idle',
    },
    deployed: {
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/20',
        dotColor: 'bg-emerald-500',
        glowColor: 'rgba(52, 211, 153, 0.5)',
        label: 'Deployed',
    },
    in_transit: {
        color: 'text-amber-400',
        bg: 'bg-amber-500/20',
        dotColor: 'bg-amber-500',
        glowColor: 'rgba(251, 191, 36, 0.5)',
        label: 'Moving',
    },
    in_battle: {
        color: 'text-red-400',
        bg: 'bg-red-500/20',
        dotColor: 'bg-red-500',
        glowColor: 'rgba(239, 68, 68, 0.6)',
        label: 'In Battle',
    },
};
// Faction Display Configuration
export const FACTION_CONFIG = {
    solar: {
        name: 'Solar Collective',
        description: 'Energy production bonus +20%',
        color: 'amber',
        gradient: 'from-amber-500 to-orange-500',
        border: 'border-amber-500/50',
        bg: 'bg-amber-500/10',
        glow: 'shadow-amber-500/20',
    },
    nano: {
        name: 'Nano Syndicate',
        description: 'Nanomaterials production +20%',
        color: 'emerald',
        gradient: 'from-emerald-500 to-teal-500',
        border: 'border-emerald-500/50',
        bg: 'bg-emerald-500/10',
        glow: 'shadow-emerald-500/20',
    },
    quantum: {
        name: 'Quantum Alliance',
        description: 'Quantum data production +20%',
        color: 'violet',
        gradient: 'from-violet-500 to-purple-500',
        border: 'border-violet-500/50',
        bg: 'bg-violet-500/10',
        glow: 'shadow-violet-500/20',
    },
    darkstar: {
        name: 'Dark Star Corp',
        description: 'Dark matter production +30%',
        color: 'rose',
        gradient: 'from-rose-500 to-pink-500',
        border: 'border-rose-500/50',
        bg: 'bg-rose-500/10',
        glow: 'shadow-rose-500/20',
    },
};
// Resource Generation Rates (per hour per zone)
export const RESOURCE_GENERATION = {
    base: {
        energy: 10,
        nanomaterials: 3,
        quantum: 1,
        darkMatter: 0.2,
    },
};
// Mining Rig level-scaled resource bonus
export const MINING_RIG_BONUS = {
    baseBonus: 0.10,
    perLevelBonus: 0.05,
    getMultiplier: (level) => {
        const clamped = Math.max(1, Math.min(level, 10));
        return 1 + 0.10 + (clamped - 1) * 0.05;
    },
};
// Deterministic per-zone resource profile based on hex ID.
// Base profile varies by hash, then biome multipliers are applied.
export function getZoneResourceProfile(hexId) {
    let hash = 0;
    for (let i = 0; i < hexId.length; i++) {
        hash = ((hash << 5) - hash + hexId.charCodeAt(i)) | 0;
    }
    const zoneTypeRoll = Math.abs(hash % 100);
    const v1 = Math.abs((hash >> 8) % 100);
    const v2 = Math.abs((hash >> 16) % 100);
    let base;
    if (zoneTypeRoll < 50) {
        base = { energy: 10 + Math.floor(v1 / 20), nanomaterials: 2 + Math.floor(v2 / 50), quantum: 1, darkMatter: 0.2 };
    }
    else if (zoneTypeRoll < 75) {
        base = { energy: 8, nanomaterials: 4 + Math.floor(v1 / 25), quantum: 1, darkMatter: 0.2 };
    }
    else if (zoneTypeRoll < 90) {
        base = { energy: 8, nanomaterials: 3, quantum: 2 + Math.floor(v1 / 33), darkMatter: 0.2 };
    }
    else {
        base = { energy: 8, nanomaterials: 3, quantum: 1, darkMatter: 0.5 + v1 / 200 };
    }
    // Apply biome multipliers
    const biome = getZoneBiome(hexId);
    return {
        energy: Math.round(base.energy * biome.resourceMultipliers.energy * 10) / 10,
        nanomaterials: Math.round(base.nanomaterials * biome.resourceMultipliers.nanomaterials * 10) / 10,
        quantum: Math.round(base.quantum * biome.resourceMultipliers.quantum * 10) / 10,
        darkMatter: Math.round(base.darkMatter * biome.resourceMultipliers.darkMatter * 100) / 100,
    };
}
// Faction Bonuses
export const FACTION_BONUSES = {
    solar: {
        name: 'Solar Collective',
        bonus: { resource: 'energy', multiplier: 1.2 },
    },
    nano: {
        name: 'Nano Syndicate',
        bonus: { resource: 'nanomaterials', multiplier: 1.2 },
    },
    quantum: {
        name: 'Quantum Alliance',
        bonus: { resource: 'quantum', multiplier: 1.2 },
    },
    darkstar: {
        name: 'Dark Star Corp',
        bonus: { resource: 'darkMatter', multiplier: 1.3 },
    },
};
/**
 * Calculate the effective resource rates for a single zone, including all bonuses.
 * Used by both server and client to ensure consistent rate display.
 */
export function getZoneRates(hexId, opts) {
    const profile = getZoneResourceProfile(hexId);
    let multiplier = 1.0;
    // Mining rig bonus
    if (opts?.structures) {
        const rig = opts.structures.find(s => s.structureType === 'mining_rig' && s.active);
        if (rig)
            multiplier = MINING_RIG_BONUS.getMultiplier(rig.level);
    }
    // Rich Deposit zone modifier
    const mods = getZoneModifiers(hexId);
    const rich = mods.find(m => m.id === 'rich_deposit');
    if (rich)
        multiplier *= (1 + rich.effects.resourceGen);
    const rates = {
        energy: profile.energy * multiplier,
        nanomaterials: profile.nanomaterials * multiplier,
        quantum: profile.quantum * multiplier,
        darkMatter: profile.darkMatter * multiplier,
    };
    // Faction bonus
    if (opts?.faction) {
        const bonus = FACTION_BONUSES[opts.faction]?.bonus;
        if (bonus) {
            rates[bonus.resource] *= bonus.multiplier;
        }
    }
    return {
        energy: Math.round(rates.energy),
        nanomaterials: Math.round(rates.nanomaterials),
        quantum: Math.round(rates.quantum),
        darkMatter: Math.round(rates.darkMatter * 10) / 10,
    };
}
// XP Requirements
export const XP_PER_LEVEL = (level) => level * 100 + 50;
// XP Rewards — single source of truth for all action XP values
export const XP_REWARDS = {
    claimZone: 25,
    buildStructure: 15,
    upgradeStructure: 20,
    createUnit: 10,
    pveVictory: 100,
    pvpVictory: 75,
};
export const UNIT_ABILITIES = {
    scout_drone: {
        name: 'Evasion Protocol',
        description: '+15% dodge chance',
        trigger: 'on_defend',
    },
    assault_bot: {
        name: 'Aggression Matrix',
        description: '+20% damage below 50% HP',
        trigger: 'on_attack',
    },
    heavy_mech: {
        name: 'Reinforced Armor',
        description: 'Takes 15% less damage',
        trigger: 'on_defend',
    },
    nano_swarm: {
        name: 'Regeneration',
        description: 'Heals 5% max HP per round',
        trigger: 'on_round_start',
    },
    emp_specialist: {
        name: 'System Disruption',
        description: '-10% target accuracy for 1 round',
        trigger: 'on_attack',
    },
    titan_mech: {
        name: 'Devastating Blow',
        description: '+30% crit multiplier',
        trigger: 'on_attack',
    },
};
// Mission Definitions
export const MISSION_DEFINITIONS = {
    claim_zones: {
        type: 'claim_zones',
        title: 'Claim 2 Zones',
        description: 'Expand your territory by claiming 2 zones',
        targetCount: 2,
        xpReward: 50,
    },
    build_structure: {
        type: 'build_structure',
        title: 'Build a Structure',
        description: 'Construct a structure in one of your zones',
        targetCount: 1,
        xpReward: 30,
    },
    create_units: {
        type: 'create_units',
        title: 'Create 3 Units',
        description: 'Build up your army with 3 new units',
        targetCount: 3,
        xpReward: 40,
    },
    win_pve: {
        type: 'win_pve',
        title: 'Win a PvE Battle',
        description: 'Defeat a corrupted zone',
        targetCount: 1,
        xpReward: 75,
    },
    upgrade_structure: {
        type: 'upgrade_structure',
        title: 'Upgrade a Structure',
        description: 'Improve one of your structures',
        targetCount: 1,
        xpReward: 40,
    },
    deploy_units: {
        type: 'deploy_units',
        title: 'Deploy 2 Units',
        description: 'Deploy 2 units to your zones',
        targetCount: 2,
        xpReward: 20,
    },
    earn_resources: {
        type: 'earn_resources',
        title: 'Earn 100 Resources',
        description: 'Accumulate 100 total resources',
        targetCount: 2,
        xpReward: 35,
    },
    reach_level: {
        type: 'reach_level',
        title: 'Level Up',
        description: 'Gain enough XP to reach the next level',
        targetCount: 1,
        xpReward: 50,
    },
};
// Max zones a player can own, scaling with level
export function getMaxZones(playerLevel) {
    return GAME_CONFIG.MAX_ZONES_BASE + playerLevel;
}
// Battle Constants
export const BATTLE_CONSTANTS = {
    FRONT_ROW_DAMAGE_TAKEN: 1.3,
    FRONT_ROW_DAMAGE_DEALT: 1.2,
    BACK_ROW_DAMAGE_DEALT: 0.9,
    FLANK_BONUS: 1.15,
    CRIT_MULTIPLIER: 2.0,
    BASE_CRIT_CHANCE: 0.15,
    DODGE_SPEED_FACTOR: 0.002,
};
// Mod Display Metadata
export const MOD_META = {
    weapon: {
        name: 'Weapon Mod',
        shortName: 'WPN',
        color: 'text-red-400',
        icon: '🗡',
        description: 'Boosts attack and accuracy',
    },
    armor: {
        name: 'Armor Mod',
        shortName: 'ARM',
        color: 'text-blue-400',
        icon: '🛡',
        description: 'Boosts defense and HP',
    },
    system: {
        name: 'System Mod',
        shortName: 'SYS',
        color: 'text-cyan-400',
        icon: '⚙',
        description: 'Boosts speed and energy',
    },
};
// Mod Rarity Configuration
export const RARITY_CONFIG = {
    common: {
        name: 'Common',
        color: 'text-slate-300',
        bg: 'bg-slate-500/20',
        border: 'border-slate-500/30',
        statMultiplier: 1,
    },
    rare: {
        name: 'Rare',
        color: 'text-blue-400',
        bg: 'bg-blue-500/20',
        border: 'border-blue-500/30',
        statMultiplier: 2,
    },
    legendary: {
        name: 'Legendary',
        color: 'text-amber-400',
        bg: 'bg-amber-500/20',
        border: 'border-amber-500/30',
        statMultiplier: 3,
    },
};
// Stat ranges for mod generation (base values, multiplied by rarity)
export const MOD_STAT_RANGES = {
    weapon: [
        { stat: 'atk', min: 3, max: 8 },
        { stat: 'acc', min: 2, max: 5 },
    ],
    armor: [
        { stat: 'def', min: 3, max: 8 },
        { stat: 'hp', min: 5, max: 15 },
    ],
    system: [
        { stat: 'spd', min: 3, max: 8 },
        { stat: 'energy', min: 3, max: 10 },
    ],
};
// Max mods per unit
export const MAX_MODS_PER_UNIT = 3;
// Structure Gameplay Effects — defines what each structure actually does
// All per-level values are additive (level 1 = base, level 10 = base * 10)
export const STRUCTURE_EFFECTS = {
    /** Laser Turret: deals flat damage to a random enemy unit each battle turn */
    laser_turret: {
        damagePerLevel: 5, // damage per turn = level * 5 (lv1=5, lv10=50)
        description: (level) => `Deals ${level * 5} damage per turn to a random enemy`,
    },
    /** Shield Generator: reduces incoming damage to defending units by a percentage */
    shield_generator: {
        damageReductionPerLevel: 0.03, // 3% per level (lv1=3%, lv10=30%)
        maxReduction: 0.30,
        description: (level) => `Reduces incoming damage by ${Math.min(level * 3, 30)}%`,
    },
    /** Stealth Field: reduces enemy hit chance when defending */
    stealth_field: {
        accuracyPenaltyPerLevel: 0.03, // 3% accuracy penalty per level (lv1=3%, lv10=30%)
        maxPenalty: 0.30,
        description: (level) => `Reduces enemy accuracy by ${Math.min(level * 3, 30)}%`,
    },
    /** Mining Rig: scales resource generation by level (+10% base, +5% per level) */
    mining_rig: {
        description: (level) => {
            const bonus = 10 + (Math.min(level, 10) - 1) * 5;
            return `+${bonus}% resource generation`;
        },
    },
    /** Research Lab: gates creation of advanced unit types */
    research_lab: {
        unitRequirements: {
            emp_specialist: 3, // requires lab level 3+
            titan_mech: 5, // requires lab level 5+
        },
        description: (level) => {
            if (level >= 5)
                return 'All units unlocked';
            if (level >= 3)
                return 'EMP Specialist unlocked';
            return `Level 3: EMP, Level 5: Titan`;
        },
    },
    /** Headquarters: boosts all defending units' HP in battle */
    headquarters: {
        hpBoostPerLevel: 0.02, // 2% HP boost per level (lv1=2%, lv10=20%)
        description: (level) => `+${level * 2}% HP for deployed units in battle`,
    },
    /** Teleport Gate: allows deploying units to any zone with a gate */
    teleport_gate: {
        description: (_level) => `Deploy units to any zone with a gate`,
    },
};
// Structure HP — used to calculate battle HP from type + level (no DB column needed)
export const STRUCTURE_HP = {
    headquarters: { baseHp: 100 },
    shield_generator: { baseHp: 80 },
    laser_turret: { baseHp: 60 },
    stealth_field: { baseHp: 50 },
    mining_rig: { baseHp: 40 },
    research_lab: { baseHp: 40 },
    teleport_gate: { baseHp: 40 },
};
export function getStructureMaxHp(structureType, level) {
    return (STRUCTURE_HP[structureType]?.baseHp ?? 40) * level;
}
export const BIOME_CONFIG = {
    volcanic: {
        id: 'volcanic',
        name: 'Volcanic',
        color: '#ef4444',
        outlineColor: '#dc2626',
        symbol: '🌋',
        resourceMultipliers: { energy: 1.5, nanomaterials: 0.8, quantum: 0.9, darkMatter: 1.0 },
    },
    crystalline: {
        id: 'crystalline',
        name: 'Crystalline',
        color: '#a78bfa',
        outlineColor: '#8b5cf6',
        symbol: '💎',
        resourceMultipliers: { energy: 0.9, nanomaterials: 0.9, quantum: 1.5, darkMatter: 1.1 },
    },
    biozone: {
        id: 'biozone',
        name: 'Biozone',
        color: '#34d399',
        outlineColor: '#10b981',
        symbol: '🌿',
        resourceMultipliers: { energy: 1.0, nanomaterials: 1.5, quantum: 0.8, darkMatter: 0.9 },
    },
    wasteland: {
        id: 'wasteland',
        name: 'Wasteland',
        color: '#78716c',
        outlineColor: '#57534e',
        symbol: '🏚',
        resourceMultipliers: { energy: 0.9, nanomaterials: 1.0, quantum: 1.0, darkMatter: 1.4 },
    },
    techscape: {
        id: 'techscape',
        name: 'Techscape',
        color: '#38bdf8',
        outlineColor: '#0ea5e9',
        symbol: '⚙',
        resourceMultipliers: { energy: 1.1, nanomaterials: 1.1, quantum: 1.1, darkMatter: 1.0 },
    },
    tundra: {
        id: 'tundra',
        name: 'Tundra',
        color: '#94a3b8',
        outlineColor: '#64748b',
        symbol: '❄',
        resourceMultipliers: { energy: 1.3, nanomaterials: 0.9, quantum: 1.0, darkMatter: 1.0 },
    },
    nexus: {
        id: 'nexus',
        name: 'Nexus',
        color: '#fbbf24',
        outlineColor: '#f59e0b',
        symbol: '✦',
        resourceMultipliers: { energy: 1.2, nanomaterials: 1.2, quantum: 1.2, darkMatter: 1.2 },
    },
};
const BIOME_TABLE = [
    { id: 'volcanic', weight: 10 },
    { id: 'crystalline', weight: 12 },
    { id: 'biozone', weight: 18 },
    { id: 'wasteland', weight: 20 },
    { id: 'techscape', weight: 15 },
    { id: 'tundra', weight: 12 },
    { id: 'nexus', weight: 13 },
];
export function getZoneBiome(hexId) {
    const rng = seededRandom(hexId + ':biome');
    const roll = rng() * 100;
    let cumulative = 0;
    for (const entry of BIOME_TABLE) {
        cumulative += entry.weight;
        if (roll < cumulative)
            return BIOME_CONFIG[entry.id];
    }
    return BIOME_CONFIG.wasteland;
}
// ═══════════════════════════════════════════
// Zone Names — deterministic from hex ID
// ═══════════════════════════════════════════
const ZONE_PREFIXES = [
    'Obsidian', 'Crystal', 'Iron', 'Neon', 'Shadow', 'Void', 'Quantum', 'Crimson',
    'Echo', 'Nova', 'Ember', 'Frost', 'Cobalt', 'Dusk', 'Zenith', 'Prism',
    'Rust', 'Ash', 'Flux', 'Storm', 'Onyx', 'Titan', 'Vex', 'Shard',
    'Drift', 'Pulse', 'Core', 'Helix', 'Rift', 'Cipher',
    'Phantom', 'Apex', 'Glitch', 'Vector', 'Arc', 'Omega',
    'Nexus', 'Spectra', 'Chrono', 'Wraith', 'Radiant', 'Aether',
    'Molten', 'Static', 'Hyper', 'Binary', 'Null', 'Plasma',
    'Rune', 'Inferno', 'Aurora', 'Thorn', 'Mirage', 'Velvet',
];
const ZONE_SUFFIXES = [
    'Ridge', 'Basin', 'Reach', 'Hollow', 'Spire', 'Depths', 'Veil',
    'Gate', 'Forge', 'Wastes', 'Terrace', 'Crater', 'Expanse', 'Shrine', 'Outpost',
    'Pinnacle', 'Haven', 'Abyss', 'Threshold', 'Junction', 'Domain', 'Sector', 'Matrix',
    'Archive', 'Breach', 'Dome', 'Vertex', 'Conduit', 'Frontier',
    'Citadel', 'Enclave', 'Perimeter', 'Station', 'Bastion', 'Complex',
    'Grid', 'Protocol', 'Array', 'Relay', 'Frame', 'Lattice',
    'Quarry', 'Vault', 'Silo', 'Bunker', 'Hub', 'Corridor',
    'Terminus', 'Core', 'Apex', 'Ward', 'Cell', 'Cluster',
];
export function getZoneName(hexId) {
    const rng = seededRandom(hexId + ':name');
    const prefix = seededPick(ZONE_PREFIXES, rng);
    const suffix = seededPick(ZONE_SUFFIXES, rng);
    // Derive a unique sector number from the hexId to guarantee no collisions
    // Use last 4 hex chars → 0-65535, then mod to a readable 3-digit sector code
    const sectorNum = parseInt(hexId.slice(-4), 16) % 1000;
    return `${prefix} ${suffix}-${sectorNum.toString().padStart(3, '0')}`;
}
const MODIFIER_POOL = [
    { id: 'high_gravity', name: 'High Gravity', symbol: '🔽', effects: { spd: -0.10 } },
    { id: 'low_gravity', name: 'Low Gravity', symbol: '🔼', effects: { spd: 0.10 } },
    { id: 'em_storm', name: 'EM Storm', symbol: '⚡', effects: { acc: -0.15 } },
    { id: 'fortified', name: 'Fortified Ground', symbol: '🛡', effects: { structureHp: 0.10 } },
    { id: 'volatile', name: 'Volatile', symbol: '💥', effects: { crit: 0.20 } },
    { id: 'dense_fog', name: 'Dense Fog', symbol: '🌫', effects: { acc: -0.20, dodge: 0.15 } },
    { id: 'rich_deposit', name: 'Rich Deposit', symbol: '💎', effects: { resourceGen: 0.25 } },
    { id: 'radiation', name: 'Radiation', symbol: '☢', effects: { hpPerRound: -0.05 } },
    { id: 'sanctuary', name: 'Sanctuary', symbol: '🕊', effects: { noPvp: 1 } },
    { id: 'anomaly', name: 'Anomaly', symbol: '🌀', effects: { allStats: 0.15 } },
];
export function getZoneModifiers(hexId) {
    const rng = seededRandom(hexId + ':mods');
    const roll = rng();
    let count = 0;
    if (roll > 0.60)
        count = 1;
    if (roll > 0.80)
        count = 2;
    const mods = [];
    const available = [...MODIFIER_POOL];
    for (let i = 0; i < count && available.length > 0; i++) {
        const idx = Math.floor(rng() * available.length);
        mods.push(available[idx]);
        available.splice(idx, 1);
    }
    return mods;
}
export const UNIT_TRAITS = [
    { id: 'aggressive', name: 'Aggressive', color: 'text-red-400', statMods: { atk: 0.15, def: -0.10 } },
    { id: 'resilient', name: 'Resilient', color: 'text-blue-400', statMods: { def: 0.15, spd: -0.10 } },
    { id: 'swift', name: 'Swift', color: 'text-cyan-400', statMods: { spd: 0.15, atk: -0.10 } },
    { id: 'precise', name: 'Precise', color: 'text-violet-400', statMods: { acc: 0.10, atk: -0.05 } },
    { id: 'armored', name: 'Armored', color: 'text-orange-400', statMods: { hp: 0.20, spd: -0.15 } },
    { id: 'volatile', name: 'Volatile', color: 'text-amber-400', statMods: { atk: 0.25, hp: -0.10 } },
    { id: 'stealthy', name: 'Stealthy', color: 'text-slate-400', statMods: { spd: 0.15, atk: -0.10 } },
    { id: 'energized', name: 'Energized', color: 'text-emerald-400', statMods: { energy: 0.20, def: -0.10 } },
    { id: 'berserker', name: 'Berserker', color: 'text-red-500', statMods: { atk: 0.20, def: -0.15 } },
    { id: 'guardian', name: 'Guardian', color: 'text-blue-500', statMods: { def: 0.20, atk: -0.10 } },
    { id: 'sniper', name: 'Sniper', color: 'text-violet-500', statMods: { acc: 0.20, spd: -0.15 } },
    { id: 'lucky', name: 'Lucky', color: 'text-amber-500', statMods: { hp: 0.05, atk: 0.05, def: 0.05, spd: 0.05, acc: 0.05 } },
];
export function getRandomTrait() {
    return UNIT_TRAITS[Math.floor(Math.random() * UNIT_TRAITS.length)];
}
export function applyTraitToStats(baseStats, trait) {
    const result = { ...baseStats };
    for (const [stat, mod] of Object.entries(trait.statMods)) {
        const key = stat;
        result[key] = Math.round(result[key] * (1 + mod));
    }
    return result;
}
export function getTraitById(traitId) {
    return UNIT_TRAITS.find(t => t.id === traitId);
}
// ═══════════════════════════════════════════
// Procedural Mission Generation
// ═══════════════════════════════════════════
const MISSION_TEMPLATES = [
    { type: 'claim_zones', titleFn: (n) => `Claim ${n} Zone${n > 1 ? 's' : ''}`, descFn: (n) => `Expand your territory by claiming ${n} zone${n > 1 ? 's' : ''}`, baseTarget: 1, levelScale: 0.3, baseXp: 25 },
    { type: 'build_structure', titleFn: (n) => `Build ${n} Structure${n > 1 ? 's' : ''}`, descFn: (n) => `Construct ${n} structure${n > 1 ? 's' : ''} in your zones`, baseTarget: 1, levelScale: 0.2, baseXp: 30 },
    { type: 'create_units', titleFn: (n) => `Create ${n} Unit${n > 1 ? 's' : ''}`, descFn: (n) => `Build up your army with ${n} new unit${n > 1 ? 's' : ''}`, baseTarget: 2, levelScale: 0.4, baseXp: 20 },
    { type: 'win_pve', titleFn: (n) => `Win ${n} PvE Battle${n > 1 ? 's' : ''}`, descFn: (n) => `Defeat ${n} corrupted zone${n > 1 ? 's' : ''}`, baseTarget: 1, levelScale: 0.15, baseXp: 75 },
    { type: 'upgrade_structure', titleFn: (n) => `Upgrade ${n} Structure${n > 1 ? 's' : ''}`, descFn: (n) => `Improve ${n} of your structure${n > 1 ? 's' : ''}`, baseTarget: 1, levelScale: 0.2, baseXp: 40 },
    { type: 'deploy_units', titleFn: (n) => `Deploy ${n} Unit${n > 1 ? 's' : ''}`, descFn: (n) => `Deploy ${n} unit${n > 1 ? 's' : ''} to your zones`, baseTarget: 2, levelScale: 0.3, baseXp: 20 },
    { type: 'earn_resources', titleFn: (n) => `Earn ${n * 50} Resources`, descFn: (n) => `Accumulate ${n * 50} total resources`, baseTarget: 2, levelScale: 0.5, baseXp: 35 },
    { type: 'reach_level', titleFn: (_n) => `Level Up`, descFn: (_n) => `Gain enough XP to reach the next level`, baseTarget: 1, levelScale: 0, baseXp: 50 },
];
export function generateMissions(playerLevel, seed) {
    const rng = seededRandom(seed);
    const count = seededRange(3, 5, rng);
    const available = [...MISSION_TEMPLATES];
    const missions = [];
    for (let i = 0; i < count && available.length > 0; i++) {
        const idx = Math.floor(rng() * available.length);
        const template = available[idx];
        available.splice(idx, 1);
        const targetCount = Math.max(1, Math.floor(template.baseTarget + playerLevel * template.levelScale));
        const xpReward = Math.floor(template.baseXp * (1 + playerLevel * 0.1));
        missions.push({
            type: template.type,
            title: template.titleFn(targetCount),
            description: template.descFn(targetCount),
            targetCount,
            xpReward,
        });
    }
    return missions;
}
// ═══════════════════════════════════════════
// Smooth AI Difficulty — continuous scaling
// ═══════════════════════════════════════════
export function getAvailableAITypes(difficulty) {
    if (difficulty >= 30)
        return ['heavy_mech', 'emp_specialist', 'titan_mech'];
    if (difficulty >= 15)
        return ['assault_bot', 'heavy_mech', 'nano_swarm', 'emp_specialist'];
    if (difficulty >= 5)
        return ['scout_drone', 'assault_bot', 'nano_swarm'];
    return ['scout_drone', 'assault_bot'];
}
export function generateAISquad(difficulty, seed) {
    const rng = seededRandom(seed);
    const unitCount = Math.min(3, 1 + Math.floor(difficulty / 10));
    const available = getAvailableAITypes(difficulty);
    const units = [];
    for (let i = 0; i < unitCount; i++) {
        const unitType = seededPick(available, rng);
        const level = Math.max(1, Math.floor(difficulty * 0.6 + rng() * difficulty * 0.4));
        units.push({ unitType, level });
    }
    return units;
}
// Export building generation module
export * from './buildingGeneration';
//# sourceMappingURL=index.js.map